import React from 'react';
import { Button } from '@/components/ui/button';
import { Building2, Users, Layers, Settings, Layout } from 'lucide-react';
import appIcon from '@/assets/app-icon.png';

interface WelcomeScreenProps {
  firstName: string;
  onStart: () => void;
  onSkip: () => void;
}

const STEP_CARDS = [
  {
    icon: Building2,
    name: 'Company Setup',
    description: 'Add the companies your resources will be scheduled against',
  },
  {
    icon: Users,
    name: 'User Access',
    description: 'Assign roles and company access to your Evo users',
  },
  {
    icon: Layers,
    name: 'Booking Types',
    description: 'Define the categories and fields used when creating bookings',
  },
  {
    icon: Settings,
    name: 'Planner Defaults',
    description:
      'Configure utilisation, forecasting, matching and scheduling defaults',
  },
  {
    icon: Layout,
    name: 'Planner Groups',
    description:
      'Organise your synced resources into logical planner groups',
  },
];

export function WelcomeScreen({ firstName, onStart, onSkip }: WelcomeScreenProps) {
  return (
    <div className="flex h-full flex-col items-center justify-center overflow-y-auto py-10 px-8">
      <div className="w-full max-w-2xl space-y-8">
        {/* Logo + Heading */}
        <div className="flex flex-col items-center gap-4 text-center">
          <div
            className="flex h-16 w-16 items-center justify-center rounded-2xl shadow-sm"
            style={{ backgroundColor: 'rgba(26, 138, 138, 0.12)' }}
          >
            <img src={appIcon} alt="Scheduler" className="h-10 w-10 rounded-lg object-cover" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Scheduler</h1>
        </div>

        {/* Greeting */}
        <div className="text-center">
          <p className="text-xl font-semibold text-foreground">
            Welcome, {firstName}! 👋
          </p>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-lg mx-auto">
            Let's get your workspace set up. This wizard will guide you through the key
            configuration steps. It only takes a few minutes and you can always come back
            to it later.
          </p>
        </div>

        {/* Step preview cards */}
        <div className="grid grid-cols-2 gap-3">
          {STEP_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.name}
                className="flex items-start gap-3 rounded-lg border border-border bg-background p-4 transition-colors hover:bg-muted/40 cursor-default"
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: 'rgba(26, 138, 138, 0.1)' }}
                >
                  <Icon className="h-4 w-4" style={{ color: '#1a8a8a' }} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground leading-tight">
                    {card.name}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-snug">
                    {card.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTAs */}
        <div className="flex flex-col items-center gap-3">
          <Button
            size="lg"
            className="px-8 text-white"
            style={{ backgroundColor: '#1a8a8a' }}
            onClick={onStart}
          >
            Let's Start →
          </Button>
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
            onClick={onSkip}
          >
            Skip onboarding
          </button>
        </div>
      </div>
    </div>
  );
}
